import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    ChevronDown,
    ChevronRight,
    Info,
    ShieldAlert,
    ShieldCheck,
    AlertTriangle,
    AlertCircle,
    CircleAlert,
    CircleMinus,
    Search,
    Crosshair,
} from 'lucide-react'
import { Input } from '@/components/ui/input'

interface AnalyticsReportDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    analyticsText: string | null
}

const ENGINE_DISPLAY_NAMES: Record<string, string> = {
    Bedrock: 'AI Analysis',
}

const ENGINE_DESCRIPTIONS: Record<string, string> = {
    ClamAV: 'Open-source antivirus engine that scans for known malware signatures, trojans, viruses, and other malicious threats.',
    YARA: 'Pattern matching engine that identifies malware families using custom rules based on textual or binary patterns.',
    Entropy: 'Analyses file entropy to detect packed, encrypted, or obfuscated content that may indicate malware or ransomware.',
    HashLookup: 'Compares file hashes against known threat intelligence databases to identify previously catalogued malicious files.',
    ConfigDiff: 'Compares current configuration against a known-good baseline to detect unauthorized changes or tampering.',
    Bedrock: 'AI-powered analysis engine using Claude to perform deep inspection of configurations and detect subtle security issues.',
    BinaryAnalysis: 'Examines binary file structure, imports, and embedded strings to identify suspicious executables or payloads.',
    FilesystemAnomaly: 'Scans filesystem metadata for unusual permissions, hidden files, timestamp anomalies, or unexpected file placements.',
    Rootkit: 'Detects rootkit indicators including hidden processes, kernel modifications, and system call table tampering.',
}

const SEVERITY_CONFIG = {
    critical: { color: 'bg-red-100 text-red-700', icon: ShieldAlert, label: 'Critical' },
    high: { color: 'bg-orange-100 text-orange-700', icon: AlertTriangle, label: 'High' },
    medium: { color: 'bg-amber-100 text-amber-700', icon: AlertCircle, label: 'Medium' },
    low: { color: 'bg-blue-100 text-blue-700', icon: CircleAlert, label: 'Low' },
    info: { color: 'bg-gray-100 text-gray-600', icon: CircleMinus, label: 'Info' },
} as const

function SeverityBadge({ severity }: { severity: string }) {
    const config = SEVERITY_CONFIG[severity as keyof typeof SEVERITY_CONFIG] || SEVERITY_CONFIG.info
    const Icon = config.icon
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
            <Icon className="h-3 w-3" />
            {config.label}
        </span>
    )
}

// MITRE ATT&CK technique → tactic map. Keyed by base technique ID; sub-techniques
// (e.g. T1070.001) fall back to their base ID. Covers the techniques the scanner
// currently emits — extend (or source from the ATT&CK STIX bundle) as coverage grows.
const MITRE_TACTICS: Record<string, string> = {
    T1486: 'Impact',
    T1490: 'Impact',
    T1561: 'Impact',
    T1542: 'Persistence / Defense Evasion',
    T1070: 'Defense Evasion',
    T1562: 'Defense Evasion',
    T1112: 'Defense Evasion',
    T1136: 'Persistence',
    T1543: 'Persistence',
    T1053: 'Execution / Persistence',
    T1078: 'Privilege Escalation',
    T1110: 'Credential Access',
    T1021: 'Lateral Movement',
    T1059: 'Execution',
    T1071: 'Command and Control',
    T1566: 'Initial Access',
}

function tacticForTechnique(id?: string | null): string | null {
    if (!id) return null
    return MITRE_TACTICS[id] || MITRE_TACTICS[id.split('.')[0]] || null
}

// T1486 → /techniques/T1486/ ; T1070.001 → /techniques/T1070/001/
function mitreUrl(id: string): string {
    return `https://attack.mitre.org/techniques/${id.replace('.', '/')}/`
}

/** Linked ATT&CK chip: technique ID (+ optional name). Renders nothing when no ID. */
function MitreBadge({ id, name, compact = false }: { id?: string | null; name?: string | null; compact?: boolean }) {
    if (!id) return null
    return (
        <a
            href={mitreUrl(id)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={name || id}
            className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-500/15 dark:text-purple-300 dark:hover:bg-purple-500/25 transition-colors"
        >
            <Crosshair className="h-3 w-3 flex-shrink-0" />
            <span className="tabular-nums">{id}</span>
            {!compact && name && <span className="hidden sm:inline truncate">&middot; {name}</span>}
        </a>
    )
}

function EngineStatusBadge({ status }: { status: string }) {
    const colors: Record<string, string> = {
        completed: 'bg-green-50 text-green-700',
        skipped: 'bg-slate-50 text-slate-500',
        not_applicable: 'bg-slate-50 text-slate-400',
        not_run: 'bg-slate-50 text-slate-400',
        error: 'bg-red-50 text-red-600',
    }
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
            {status.replace(/_/g, ' ')}
        </span>
    )
}

function formatDuration(seconds: number | undefined | null): string {
    if (seconds == null) return '-'
    if (seconds < 1) return '<1s'
    return `${seconds.toFixed(1)}s`
}

export function AnalyticsReportDialog({ open, onOpenChange, analyticsText }: AnalyticsReportDialogProps) {
    const { t } = useTranslation()
    const [expandedFindings, setExpandedFindings] = useState<Set<number>>(new Set())
    const [search, setSearch] = useState('')

    let data: any = null
    try {
        if (analyticsText) data = JSON.parse(analyticsText)
    } catch {
        // invalid JSON
    }

    if (!data) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{t('objects.versions.analyticsReport')}</DialogTitle>
                        <DialogDescription>{t('objects.versions.noAnalyticsData')}</DialogDescription>
                    </DialogHeader>
                </DialogContent>
            </Dialog>
        )
    }

    const metadata = data.metadata || {}
    const summary = data.summary || {}
    const severityCounts = summary.severity_counts || {}
    const engines = data.engines || {}
    // Support both 'findings' and 'top_findings' field names
    const findings = data.findings || data.top_findings || []
    const scanStatus = data.status || 'unknown'
    // Pipeline errors explain why engines didn't run (e.g. "Failed to mount disk image")
    const pipelineFindings = findings.filter((f: any) => f.engine === 'pipeline')
    const hasEngineFailure = Object.values(engines).every((e: any) => e.status === 'not_run') && pipelineFindings.length > 0

    const toggleFinding = (idx: number) => {
        setExpandedFindings(prev => {
            const next = new Set(prev)
            if (next.has(idx)) next.delete(idx)
            else next.add(idx)
            return next
        })
    }

    // Authoritative MITRE techniques for the whole scan (backend-enriched from
    // analytics_findings, since the compact report findings drop `details`).
    const reportTechniques: Array<{ id: string; name?: string | null; count?: number }> = data.attack_techniques || []

    // Distinct ATT&CK tactics present in this scan — from the scan's techniques plus
    // any per-finding mitre that made it into the displayed findings.
    const observedTactics = Array.from(
        new Set(
            [
                ...reportTechniques.map((tt) => tacticForTechnique(tt.id)),
                ...findings.map((f: any) => tacticForTechnique(f.details?.mitre_attack)),
            ].filter(Boolean)
        )
    ) as string[]

    const q = search.toLowerCase()
    const filteredFindings = search
        ? findings.filter((f: any) =>
            f.title?.toLowerCase().includes(q) ||
            f.description?.toLowerCase().includes(q) ||
            f.severity?.toLowerCase().includes(q) ||
            f.engine?.toLowerCase().includes(q) ||
            f.details?.mitre_attack?.toLowerCase().includes(q) ||
            f.details?.mitre_technique?.toLowerCase().includes(q) ||
            (tacticForTechnique(f.details?.mitre_attack) || '').toLowerCase().includes(q)
        )
        : findings

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setSearch('')
            setExpandedFindings(new Set())
        }
        onOpenChange(open)
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {scanStatus === 'clean' ? (
                            <ShieldCheck className="h-5 w-5 text-green-500" />
                        ) : (
                            <ShieldAlert className="h-5 w-5 text-red-500" />
                        )}
                        {t('objects.versions.analyticsReport')}
                    </DialogTitle>
                    <DialogDescription>
                        {data.scan_type && `${data.scan_type.charAt(0).toUpperCase() + data.scan_type.slice(1)} scan`}
                        {data.scan_id && ` \u2022 ${data.scan_id}`}
                        {data.duration_seconds != null && ` \u2022 ${data.duration_seconds}s`}
                    </DialogDescription>
                </DialogHeader>

                <div className="overflow-y-auto flex-1 space-y-5 pr-1">
                    {/* Summary cards */}
                    {summary.total_findings != null && (
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(severityCounts).map(([severity, count]) => {
                                const config = SEVERITY_CONFIG[severity as keyof typeof SEVERITY_CONFIG] || SEVERITY_CONFIG.info
                                const Icon = config.icon
                                return (
                                    <div key={severity} className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 ${config.color}`}>
                                        <Icon className="h-3.5 w-3.5" />
                                        <span className="text-xs font-medium">{config.label}</span>
                                        <span className="text-sm font-bold">{count as number}</span>
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {/* ATT&CK tactics observed */}
                    {observedTactics.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-medium text-muted-foreground">
                                {t('objects.versions.attackTacticsObserved', 'ATT&CK tactics observed')}:
                            </span>
                            {observedTactics.map((tactic) => (
                                <span
                                    key={tactic}
                                    className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300"
                                >
                                    <Crosshair className="h-3 w-3" />
                                    {tactic}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* ATT&CK techniques observed (whole scan) */}
                    {reportTechniques.length > 0 && (
                        <div>
                            <h3 className="text-sm font-semibold mb-2">{t('objects.versions.attackTechniques', 'ATT&CK techniques')}</h3>
                            <div className="flex flex-wrap gap-2">
                                {reportTechniques.map((tt) => {
                                    const tactic = tacticForTechnique(tt.id)
                                    return (
                                        <span key={tt.id} className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs">
                                            <MitreBadge id={tt.id} name={tt.name} />
                                            {tactic && <span className="text-muted-foreground">{tactic}</span>}
                                            {tt.count != null && tt.count > 1 && (
                                                <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-muted-foreground">{tt.count}</span>
                                            )}
                                        </span>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                    {/* Pipeline error — explains why engines didn't run */}
                    {hasEngineFailure && pipelineFindings.map((f: any, i: number) => (
                        <div key={i} className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 p-3">
                            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="font-medium text-sm text-red-700 dark:text-red-400">{f.title}</div>
                                <div className="text-xs text-red-600 dark:text-red-400/80 mt-0.5">{f.description}</div>
                                {f.timestamp && <div className="text-[10px] text-muted-foreground mt-1">{new Date(f.timestamp).toLocaleString()}</div>}
                            </div>
                        </div>
                    ))}

                    {/* Scanning Engines Table */}
                    <div>
                        <div>
                            <h3 className="text-sm font-semibold mb-2">{t('objects.versions.scanningEngines')}</h3>
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-muted/50 border-b">
                                            <th className="text-left px-3 py-2 font-medium">Engine</th>
                                            <th className="text-center px-3 py-2 font-medium">Status</th>
                                            <th className="text-center px-3 py-2 font-medium">Findings</th>
                                            <th className="text-center px-3 py-2 font-medium">Duration</th>
                                            <th className="text-left px-3 py-2 font-medium">Detail</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(engines).map(([name, engine]: [string, any], idx: number) => (
                                            <tr key={name} className={idx % 2 === 0 ? '' : 'bg-muted/20'}>
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="font-medium">{ENGINE_DISPLAY_NAMES[name] || name}</span>
                                                        <span className="relative group/tip inline-flex">
                                                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0" />
                                                            <span className="absolute left-6 top-1/2 -translate-y-1/2 z-[400] hidden group-hover/tip:block w-56 px-2.5 py-1.5 rounded-md border bg-popover text-popover-foreground text-xs shadow-md">
                                                                {ENGINE_DESCRIPTIONS[name] || `${name} scanning engine`}
                                                            </span>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    <EngineStatusBadge status={engine.status} />
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    {engine.findings != null && engine.findings > 0 ? (
                                                        <span className="text-xs font-semibold text-red-600">{engine.findings}</span>
                                                    ) : engine.findings === 0 ? (
                                                        <span className="text-xs text-muted-foreground">0</span>
                                                    ) : (
                                                        <span className="text-xs text-muted-foreground">-</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    <span className="text-xs text-muted-foreground">
                                                        {formatDuration(engine.duration)}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span className="text-xs text-muted-foreground">
                                                        {engine.reason || ''}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Device Information */}
                    {Object.keys(metadata).length > 0 && (
                        <div>
                            <h3 className="text-sm font-semibold mb-2">{t('objects.versions.deviceInformation')}</h3>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-1 p-3 text-sm">
                                {metadata.device_name && (
                                    <>
                                        <span className="text-muted-foreground">Device</span>
                                        <span className="font-medium">{metadata.device_name}</span>
                                    </>
                                )}
                                {metadata.manufacturer && (
                                    <>
                                        <span className="text-muted-foreground">Manufacturer</span>
                                        <span className="font-medium">{metadata.manufacturer}</span>
                                    </>
                                )}
                                {metadata.model && (
                                    <>
                                        <span className="text-muted-foreground">Model</span>
                                        <span className="font-medium">{metadata.model}</span>
                                    </>
                                )}
                                {metadata.software_version && (
                                    <>
                                        <span className="text-muted-foreground">Software</span>
                                        <span className="font-medium">{metadata.software_version}</span>
                                    </>
                                )}
                                {metadata.ip_address && (
                                    <>
                                        <span className="text-muted-foreground">IP Address</span>
                                        <span className="font-medium">{metadata.ip_address}</span>
                                    </>
                                )}
                                {metadata.default_gateway && (
                                    <>
                                        <span className="text-muted-foreground">Gateway</span>
                                        <span className="font-medium">{metadata.default_gateway}</span>
                                    </>
                                )}
                                {metadata.software_image_file && (
                                    <>
                                        <span className="text-muted-foreground">Image</span>
                                        <span className="font-medium truncate">{metadata.software_image_file}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Findings */}
                    {findings.length > 0 && (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-semibold">
                                    {t('objects.versions.findings')} ({findings.length})
                                </h3>
                            </div>
                            <div className="relative mb-3">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder={t('common.search')}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-9 h-8 text-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                {filteredFindings.map((finding: any, idx: number) => {
                                    const isExpanded = expandedFindings.has(idx)
                                    return (
                                        <div key={idx} className="border rounded-lg overflow-hidden">
                                            <button
                                                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
                                                onClick={() => toggleFinding(idx)}
                                            >
                                                {isExpanded ? (
                                                    <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                                                ) : (
                                                    <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                                                )}
                                                <SeverityBadge severity={finding.severity} />
                                                <span className="text-sm font-medium flex-1 truncate">{finding.title}</span>
                                                {finding.details?.mitre_attack && (
                                                    <span className="flex-shrink-0">
                                                        <MitreBadge id={finding.details.mitre_attack} name={finding.details.mitre_technique} compact />
                                                    </span>
                                                )}
                                                {finding.engine && (
                                                    <span className="text-xs text-muted-foreground flex-shrink-0">{finding.engine}</span>
                                                )}
                                            </button>
                                            {isExpanded && (
                                                <div className="px-4 pb-3 pt-1 border-t bg-muted/30 space-y-3">
                                                    <p className="text-sm">{finding.description}</p>
                                                    {finding.details?.mitre_attack && (
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <MitreBadge id={finding.details.mitre_attack} name={finding.details.mitre_technique} />
                                                            {tacticForTechnique(finding.details.mitre_attack) && (
                                                                <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
                                                                    {tacticForTechnique(finding.details.mitre_attack)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {finding.details?.line && (
                                                        <div>
                                                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Configuration</span>
                                                            <pre className="mt-1 bg-muted rounded p-2 text-xs font-mono whitespace-pre-wrap break-words overflow-x-auto">
                                                                {finding.details.line}
                                                            </pre>
                                                        </div>
                                                    )}
                                                    {finding.timestamp && (
                                                        <span className="text-xs text-muted-foreground">
                                                            {new Date(finding.timestamp).toLocaleString()}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                                {filteredFindings.length === 0 && search && (
                                    <p className="text-sm text-muted-foreground text-center py-4">No findings match your search.</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
